import React, { useContext, useEffect, useMemo, useState } from "react";
import { Link, useLocation, useMatch, useNavigate } from "react-router-dom";
import { ColDef } from "ag-grid-community";
import { Copy, Eye, MoreHorizontal, Plus, Search, UserCheck } from "lucide-react";
import { toast } from "react-toastify";

import PaginationLayout from "../components/PaginationLayout";
import profile_placeholder from "../assets/images/profile_placeholder.png";
import henceforthApi from "../utils/henceforthApi";
import { GlobalContext } from "../context/Provider";

import AgGridTable from "../components/ui/AgGridTable";
import { Input } from "../components/ui/input";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "../components/ui/dropdown-menu";

interface StaffData {
  _id: string;
  name: string;
  email: string;
  image: string;
  roles: string | string[];
}

export default () => {
  const navigate = useNavigate();
  const location = useLocation();
  const match: any = useMatch("/staff/:page/:role");
  const newParam = new URLSearchParams(location.search);
  const limit = 10;
  const { authState, staffMembers, handleFilter, handleError } = useContext(GlobalContext);

  const [state, setState] = useState({
    data: [] as Array<StaffData>,
    total_count: 0,
  });
  const [loading, setLoading] = useState(false);

  if (authState.access_token) {
    henceforthApi.setToken(authState.access_token);
  }

  const currentRole = match?.params?.role || "all";
  const currentPage = match?.params?.page || "1";

  const onChangePagination = (newVal: number) => {
    if (newParam.has("search")) {
      navigate(`/staff/${newVal}/${currentRole}?search=${newParam.get("search")}`);
    } else {
      navigate(`/staff/${newVal}/${currentRole}`);
    }
  };

  const onChangeRole = (role: string) => {
    if (newParam.has("search")) {
      navigate(`/staff/1/${role}?search=${newParam.get("search")}`);
    } else {
      navigate(`/staff/1/${role}`);
    }
  };

  useEffect(() => {
    const init = async () => {
      setLoading(true);
      let searchData = newParam.has("search") ? newParam.get("search") : "";
      try {
        let apiRes = await henceforthApi.Staff.pagination(
          currentPage,
          String(searchData),
          currentRole
        );
        setState({
          ...state,
          ...apiRes.data,
        });
      } catch (error: any) {
        handleError(error);
      } finally {
        setLoading(false);
      }
    };
    init();
  }, [currentPage, currentRole, newParam.get("search")]);

  const columnDefs = useMemo<ColDef<StaffData>[]>(() => {
    const pageNum = Number(currentPage) || 1;
    return [
      {
        headerName: "Sr No.",
        field: "_id",
        width: 75,
        minWidth: 70,
        maxWidth: 85,
        suppressSizeToFit: true,
        valueGetter: (p) => {
          if (!p.node || p.node.rowIndex === null || p.node.rowIndex === undefined) return "";
          return (pageNum - 1) * limit + (p.node.rowIndex + 1);
        },
      },
      {
        headerName: "Staff Member",
        field: "name",
        minWidth: 220,
        flex: 1.5,
        cellRenderer: (p: any) => {
          const pic =
            p.data?.image && p.data.image !== "null"
              ? `${henceforthApi.API_FILE_ROOT_ORIGINAL}${p.data.image}`
              : profile_placeholder;
          const name = p.data?.name || "Not Available";
          return (
            <div className="flex items-center gap-2.5 py-1">
              <img
                src={pic}
                alt=""
                className="w-9 h-9 rounded-full object-cover border border-slate-200 shrink-0"
              />
              <div className="min-w-0">
                <span className="font-medium text-slate-800 text-sm truncate block">
                  {name}
                </span>
              </div>
            </div>
          );
        },
      },
      {
        headerName: "Email",
        field: "email",
        minWidth: 220,
        flex: 1.5,
        cellRenderer: (p: any) => {
          const email = p.value;
          if (!email) return <span className="text-slate-400">Not available</span>;
          return (
            <div className="flex items-center gap-1.5 py-1 text-sm text-slate-600">
              <span className="truncate max-w-[200px]">{email}</span>
              <Button
                type="button"
                variant="outline"
                className="p-1 text-slate-400 hover:text-slate-700 transition"
                title="Copy email"
                onClick={(e) => {
                  e.stopPropagation();
                  navigator.clipboard.writeText(email);
                  toast.success("Email copied to clipboard");
                }}
              >
                <Copy className="w-3.5 h-3.5" />
              </Button>
            </div>
          );
        },
      },
      {
        headerName: "Role",
        field: "roles",
        minWidth: 160,
        flex: 1,
        cellRenderer: (p: any) => {
          const roles = p.value;
          const roleStr = Array.isArray(roles) ? roles.join(", ") : String(roles || "Staff");
          const formatted = roleStr.replace(/_/g, " ");
          return (
            <div className="flex items-center h-full">
              <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-sm font-medium bg-blue-50 text-blue-700 border border-blue-200/60 capitalize">
                {formatted}
              </span>
            </div>
          );
        },
      },
      {
        headerName: "Actions",
        width: 90,
        minWidth: 80,
        maxWidth: 100,
        pinned: "right",
        cellRenderer: (p: any) => {
          const row = p.data;
          const staffId = row?._id;

          return (
            <div className="flex items-center justify-center h-full">
              <button
                type="button"
                onClick={() => navigate(`/staff/${staffId}`)}
                title="View Details"
                className="w-8 h-8 rounded-lg flex items-center justify-center text-slate-500 hover:text-[#006747] hover:bg-[#006747]/10 transition-colors cursor-pointer"
              >
                <Eye className="w-4 h-4" />
              </button>
            </div>
          );
        },
      },
    ];
  }, [currentPage]);

  return (
    <>
      {/* Compact Single Toolbar Row */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3 mb-4">
        {/* Left: Search Input */}
        <div className="relative w-64">
          <Search className="w-4 h-4 absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none" />
          <Input
            placeholder="Search name, email..."
            value={newParam.get("search") || ""}
            onChange={(e) => handleFilter("search", e.target.value)}
            className="search-icon-input h-9 text-sm bg-white border-slate-200 focus:bg-white shadow-2xs"
          />
        </div>

        {/* Right: Role Filter + Add Staff Button */}
        <div className="flex items-center gap-2 flex-wrap">
          {/* Role Filter */}
          <div className="w-48">
            <Select
              value={currentRole}
              onValueChange={(val) => onChangeRole(val)}
            >
              <SelectTrigger className="h-9 text-sm bg-white shadow-2xs">
                <SelectValue placeholder="Filter by Role" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Roles</SelectItem>
                {Array.isArray(staffMembers) &&
                  staffMembers.map((role: string) => (
                    <SelectItem key={role} value={role}>
                      {String(role).replace(/_/g, " ")}
                    </SelectItem>
                  ))}
              </SelectContent>
            </Select>
          </div>

          {/* Add Staff Button */}
          <Link to="/staff/add">
            <Button
              variant="default"
              size="sm"
              className="h-9 px-4 text-sm gap-1.5 shadow-xs"
            >
              <Plus className="w-4 h-4" />
              <span>Add Staff</span>
            </Button>
          </Link>
        </div>
      </div>

      {/* AG Grid Table */}
      <AgGridTable
        rowData={state.data}
        columnDefs={columnDefs}
        loading={loading}
        domLayout="autoHeight"
        emptyMessage="No staff members found"
      />

      <div className="mt-3">
        <PaginationLayout
          count={state.total_count}
          data={state.data}
          page={Number(currentPage)}
          limit={limit}
          loading={loading}
          onPageChange={(val) => onChangePagination(val)}
        />
      </div>
    </>
  );
};
